import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GetClientsByEmail } from "./services/clientService";
import { Client } from "./interfaces/client";
import ClientSelector from "./components/client-components/ClientSelector";

export default function AuthCallback() {
  const [showClientSelector, setShowClientSelector] = useState(false);
  const [availableClients, setAvailableClients] = useState<Client[]>([]);
  const { isAuthenticated, isLoading, error, getAccessTokenSilently } =
    useAuth0();
  const [errorMessage, setErrorMessage] = useState("");
  const navigate = useNavigate();

  const API_URL = process.env.REACT_APP_API_URL || "";
  const DEV_API_URL = process.env.REACT_APP_DEV_API_URL || "";
  const PROD_APP_URL = process.env.REACT_APP_PROD_APP_URL || "";
  const PROD_API_URL = process.env.REACT_APP_PROD_API_URL || "";

  const apiBaseUrl =
    window.location.hostname === "localhost"
      ? `${API_URL}/api/auth/validate`
      : window.location.hostname === new URL(PROD_APP_URL).hostname
        ? `${PROD_API_URL}/api/auth/validate`
        : `${DEV_API_URL}/api/auth/validate`;

  useEffect(() => {
    (async () => {
      if (isLoading) return;

      if (error) {
        console.error("Auth0 Callback Error:", error);
        navigate("/login", {
          state: { failMessage: `Authentication error: ${error.message}` },
        });
        return;
      }

      const portal = localStorage.getItem("pendingPortal") || "";
      const attemptID = localStorage.getItem("pendingAttemptID") || "";

      if (!portal) {
        navigate("/login");
        return;
      }

      if (!isAuthenticated) {
        navigate("/login", {
          state: { failMessage: "Login did not complete. Please try again." },
        });
        return;
      }

      try {
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: process.env.REACT_APP_AUTH0_AUDIENCE,
            scope: "openid profile email",
          },
        });

        const resp = await fetch(`${apiBaseUrl}-auth0`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ accessToken: token, portal, attemptID }),
        });

        const result = await resp
          .json()
          .catch(() => ({ message: "Non-JSON response from backend." }));

        if (!resp.ok || result?.message !== "User validated successfully.") {
          localStorage.clear();
          navigate("/login", {
            state: { failMessage: result?.message ?? "Login failed." },
          });
          return;
        }

        localStorage.setItem("token", token);
        localStorage.setItem("portalType", portal);
        const email = localStorage.getItem("pendingEmail") || "";

        localStorage.removeItem("pendingPortal");
        localStorage.removeItem("pendingEmail");
        localStorage.removeItem("pendingAttemptID");

        if (portal === "client") {
          const clients = await GetClientsByEmail(email);
          if (clients.length === 0) {
            setErrorMessage(
              "User not found or account is inactive. If you have had an account previously with Oxford using this email, we have been notified of your attempt and will reach out with more details.",
            );
            return;
          } else if (clients.length === 1) {
            localStorage.setItem("clientID", clients[0].clientID.toString());
            window.location.href = `/${portal}`;
          } else {
            setAvailableClients(clients);
            setShowClientSelector(true);
          }
        } else {
          window.location.href = `/${portal}`;
        }
      } catch {
        navigate("/login", {
          state: { failMessage: "Could not complete login. Please try again." },
        });
      }
    })();
  }, [isAuthenticated, isLoading, getAccessTokenSilently, navigate]);

  const handleClientSelected = (clientID: number) => {
    localStorage.setItem("clientID", clientID.toString());
    setShowClientSelector(false);
    window.location.href = `/client`;
  };

  const handleClientSelectorClosed = () => {
    localStorage.clear();
    window.location.href = "/login";
  };

  return (
    <>
      <div>
        {showClientSelector && (
          <ClientSelector
            clients={availableClients}
            onSelect={handleClientSelected}
            onClose={handleClientSelectorClosed}
          />
        )}
        {errorMessage && (
          <div
            className="alert-message alert alert-danger alert-dismissible fade show"
            role="alert"
          >
            {errorMessage}
            <button
              type="button"
              className="btn-close"
              onClick={() => setErrorMessage("")}
              aria-label="Close"
            ></button>
          </div>
        )}
      </div>
    </>
  );
}
